import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Section } from '../types';
import useAppContext from '../../../hooks/useAppContext';
import useUserMutation from '../../SignUp/hooks/useUserMutation';
import { AlertType } from '../../../design-system/DSAlert';
import { ArtistProfileTemplate, SectionFieldType } from '../../../api/public';

const AUTOSAVE_DELAY = 1000;

const buildDefaultDataFromTemplate = (
  template: ArtistProfileTemplate,
): Record<string, string> => {
  const data: Record<string, string> = {};
  for (const field of template.fields) {
    data[field.key] = field.type === SectionFieldType.SELECT ? 'left' : '';
  }
  return data;
};

const useCustomSections = (templates: ArtistProfileTemplate[]) => {
  const { user, setUser, showAlert } = useAppContext();

  const publishedSections = user?.artistProfile?.customSections ?? [];
  const draftSections = user?.artistProfile?.draftCustomSections;

  const [sections, setSections] = useState<Section[]>(
    () => draftSections ?? publishedSections,
  );

  const savedSnapshotRef = useRef<string>(JSON.stringify(publishedSections));

  const resetFromPublished = useCallback(() => {
    const current =
      user?.artistProfile?.draftCustomSections ??
      user?.artistProfile?.customSections ??
      [];
    setSections(current);
    savedSnapshotRef.current = JSON.stringify(current);
  }, [
    user?.artistProfile?.draftCustomSections,
    user?.artistProfile?.customSections,
  ]);

  const pendingSaveSnapshotRef = useRef<string | null>(null);

  const { mutate: saveToServer } = useUserMutation(user?.id, {
    optimistic: false,
    onSuccess: () => {
      if (pendingSaveSnapshotRef.current) {
        savedSnapshotRef.current = pendingSaveSnapshotRef.current;
        pendingSaveSnapshotRef.current = null;
      }
      setSections((currentSections) => {
        setUser((previousUser) =>
          previousUser
            ? {
                ...previousUser,
                artistProfile: {
                  customSections: [],
                  ...previousUser.artistProfile,
                  draftCustomSections: currentSections,
                },
              }
            : previousUser,
        );
        return currentSections;
      });
    },
  });

  const pendingPublishSnapshotRef = useRef<string | null>(null);

  const { mutate: publishToServer, isPending: isPublishing } = useUserMutation(
    user?.id,
    {
      optimistic: false,
      onSuccess: () => {
        if (pendingPublishSnapshotRef.current) {
          savedSnapshotRef.current = pendingPublishSnapshotRef.current;
          pendingPublishSnapshotRef.current = null;
        }
      },
      onError: () => {
        showAlert({
          message: 'Failed to publish sections. Please try again.',
          alertType: AlertType.ERROR,
        });
        // Revert the user's customSections to the previous published state
        const previousSections = user?.artistProfile?.customSections ?? [];
        setUser((previousUser) =>
          previousUser
            ? {
                ...previousUser,
                artistProfile: {
                  ...previousUser.artistProfile,
                  customSections: previousSections,
                },
              }
            : previousUser,
        );
      },
    },
  );

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(() => {
      const currentSnapshot = JSON.stringify(sections);
      if (currentSnapshot !== savedSnapshotRef.current) {
        pendingSaveSnapshotRef.current = currentSnapshot;
        saveToServer({
          artistProfile: {
            ...user?.artistProfile,
            customSections: publishedSections,
            draftCustomSections: sections,
          },
        });
      }
    }, AUTOSAVE_DELAY);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [sections, saveToServer, user?.artistProfile]);

  const addSection = useCallback(
    (type: string) => {
      const template = templates.find(
        (profileTemplate) => profileTemplate.key === type,
      );
      if (!template) return;

      const newSection: Section = {
        id: crypto.randomUUID(),
        type,
        data: buildDefaultDataFromTemplate(template),
      };
      setSections((previous) => [...previous, newSection]);
    },
    [templates],
  );

  const removeSection = useCallback((id: string) => {
    setSections((previous) => previous.filter((section) => section.id !== id));
  }, []);

  const updateSectionData = useCallback(
    (id: string, partialData: Record<string, string>) => {
      setSections((previous) =>
        previous.map((section) =>
          section.id === id
            ? { ...section, data: { ...section.data, ...partialData } }
            : section,
        ),
      );
    },
    [],
  );

  const publish = useCallback(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    pendingPublishSnapshotRef.current = JSON.stringify(sections);
    publishToServer({
      artistProfile: {
        ...user?.artistProfile,
        customSections: sections,
        draftCustomSections: sections,
      },
    });
  }, [sections, publishToServer, user?.artistProfile]);

  const hasSections = sections.length > 0;

  const [activeUploads, setActiveUploads] = useState(0);
  const uploadCallbacks = useMemo(
    () => ({
      onUploadStart: () => setActiveUploads((count) => count + 1),
      onUploadEnd: () => setActiveUploads((count) => Math.max(0, count - 1)),
    }),
    [],
  );
  const hasActiveUploads = activeUploads > 0;

  return {
    sections,
    setSections,
    addSection,
    removeSection,
    updateSectionData,
    publish,
    isPublishing,
    hasSections,
    hasActiveUploads,
    uploadCallbacks,
    resetFromPublished,
  };
};

export default useCustomSections;
